import { type ComponentProps, type ReactNode, useState } from "react";

/**
 * Whether an image the browser has finished with came to nothing. A
 * picture in the server's markup can fail before React is attached to it,
 * and its error event is then nobody's; what it leaves behind is a
 * complete image with no width.
 */
export function cameToNothing(image: HTMLImageElement): boolean {
  return Boolean(image.getAttribute("src")) && image.complete && image.naturalWidth === 0;
}

/**
 * An `<img>` that leaves nothing behind when its bytes will not come.
 *
 * A signed address expires while a tab sits open; a connection goes; a
 * thumbnail is still being made. The browser's answer to any of those is
 * its broken-image mark — Chrome draws it even on an image with no alt —
 * sitting in the corner of the placeholder the picture was going to cover:
 * seen in a day's card, a calendar cell, an album's pile, an avatar's disc.
 * The placeholder under the picture already says "a picture goes here";
 * the mark on top of it says "and something is wrong", which nobody can act
 * on. So a picture that fails gives its place back to what it sat on, or
 * to the `fallback` its owner draws when there is no picture at all.
 *
 * A new address is a new chance: the failure is forgotten when `src`
 * changes, so a refetch that brings a fresh signed URL shows the picture.
 *
 * And not draggable, unless its owner says so. Every one of these sits in
 * or beside a control — a day's cell, a pile, a grid's cell, an avatar —
 * and a mouse seldom comes down and up on the same pixel: on an image the
 * browser read the drift as the start of a drag, a ghost of the picture
 * lifted off, and the press underneath never happened. The attribute is
 * what Firefox reads; index.css says the same for the other engines.
 */
export function Picture({
  fallback = null,
  alt,
  onError,
  draggable = false,
  ...props
}: ComponentProps<"img"> & { alt: string; fallback?: ReactNode }) {
  const [brokenSrc, setBrokenSrc] = useState<string | undefined>();
  if (brokenSrc !== undefined && brokenSrc === props.src) return fallback;
  return (
    <img
      {...props}
      alt={alt}
      draggable={draggable}
      // A failure that happened before this was attached — the server's
      // markup, hydrated late — has no event to catch; it is read off the
      // element instead.
      ref={(node) => {
        if (node && cameToNothing(node)) setBrokenSrc(props.src);
      }}
      onError={(event) => {
        onError?.(event);
        setBrokenSrc(props.src);
      }}
    />
  );
}
